import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '../lib/api.js';

// Design-Spec 3c: Split-Layout, links generische Navy-Deko (KEIN Kundenname/-logo), rechts
// zwei Karten — (1) Entra-SSO für mindsquare-Mitarbeiter, (2) Magic-Link für externe Nutzer.
export function LoginPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await request('/v1/auth/magic-link/request', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    } catch {
      // Intentionally neutral: never reveal whether the email is registered or whether the
      // request failed (enumeration protection, Design-Spec 3c). Errors are not surfaced.
    } finally {
      // Neutrale Bestätigung unabhängig vom Ergebnis — kein Enumeration-Leak über registrierte Adressen.
      setSubmitting(false);
      setSent(true);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <aside
        className="relative hidden overflow-hidden md:flex md:flex-col md:justify-between md:p-12"
        style={{ background: 'linear-gradient(160deg,#1B365D,#14284A,#0F1D33)' }}
      >
        {/* Warmer Lichtschein oben rechts — gibt der Navy-Fläche Tiefe statt Flachheit. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 h-[36rem] w-[36rem] rounded-full"
          style={{
            background: 'radial-gradient(closest-side,#EA5B0B,transparent 70%)',
            opacity: 0.14,
          }}
        />
        {/* Überdimensionale, sehr blasse Bildmarke als Textur (die gestapelten Quadrate). */}
        <img
          src="/testflow-logo-dark.svg"
          alt=""
          aria-hidden
          className="pointer-events-none absolute -bottom-16 -right-12 w-[26rem] opacity-[0.06]"
        />

        {/* Produktlogo — groß, mit dem Raum, den die leere Fläche hergibt. */}
        <img
          src="/testflow-lockup-dark.svg"
          alt={t('app.name')}
          className="relative w-[min(22rem,66%)]"
        />

        {/* Claim unten verankert. */}
        <p className="relative max-w-sm font-sans text-3xl font-light leading-snug text-white/90">
          {t('login.claim')}
        </p>
      </aside>
      <main className="flex items-center justify-center bg-bg-page p-8">
        <div className="w-full max-w-sm space-y-6">
          {/* Marke nur auf Mobile — auf Desktop steht das große Logo links. */}
          <h1 className="flex justify-center md:hidden">
            <img
              src="/testflow-lockup.svg"
              alt={t('app.name')}
              className="h-10 w-auto dark:hidden"
            />
            <img
              src="/testflow-lockup-dark.svg"
              alt={t('app.name')}
              className="hidden h-10 w-auto dark:block"
            />
          </h1>

          <div className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-6">
            <h2 className="text-sm font-bold text-text-primary">{t('login.internal')}</h2>
            <a
              href="/api/v1/auth/login"
              className="block rounded-btn bg-brand-primary px-4 py-3 text-center text-white"
            >
              {t('login.internal')}
            </a>
          </div>

          <form
            onSubmit={submit}
            className="space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-6"
          >
            <h2 className="text-sm font-bold text-text-primary">{t('login.external')}</h2>
            <label className="block text-sm text-text-body" htmlFor="login-email">
              {t('login.emailLabel')}
            </label>
            <input
              id="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-3 py-2 text-text-body"
            />
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-btn bg-brand-accent px-4 py-3 text-white disabled:opacity-60"
            >
              {t('login.sendLink')}
            </button>
            {sent && <p className="text-sm text-text-muted">✓ {t('login.confirmSent')}</p>}
          </form>

          <div className="flex justify-center gap-2 text-xs text-text-muted">
            {(['de', 'en'] as const).map((lng) => (
              <button
                key={lng}
                type="button"
                onClick={() => void i18n.changeLanguage(lng)}
                className={`rounded-chip px-2 py-1 uppercase ${
                  i18n.resolvedLanguage === lng
                    ? 'bg-brand-accent text-white'
                    : 'hover:text-text-body'
                }`}
              >
                {lng}
              </button>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
